import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  MEAL_TITLE,
  formatDecimal,
  formatShortDate,
  type Meal,
} from '@jungles/shared';
import { foodNutritionLabel, displayFoodBrand, foodPortionGrams, foodSelection, unifiedFoods, type SearchFood, type FoodSearchResponse } from '../lib/food-search';
import { useApi, useDebounced } from '../lib/hooks';
import { Loader, Screen } from '../components/Chrome';
import { BarcodeIcon, SearchIcon } from '../components/icons';

/** N4 · Поиск продукта — шторка на весь экран (SPEC §5). */

function Highlighted({ text, query }: { text: string; query: string }) {
  const needle = query.trim().toLowerCase();
  if (!needle) return <>{text}</>;
  const index = text.toLowerCase().indexOf(needle);
  if (index < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, index)}
      <mark>{text.slice(index, index + needle.length)}</mark>
      {text.slice(index + needle.length)}
    </>
  );
}

export function FoodSearchScreen() {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const meal = (params.get('meal') ?? 'snack') as Meal;
  const date = params.get('date') ?? '';
  const [query, setQuery] = useState('');
  const [offset, setOffset] = useState(0);
  const debounced = useDebounced(query, 600);

  const { data: response, loading, error } = useApi<FoodSearchResponse>(
    `/api/foods?q=${encodeURIComponent(debounced.trim())}&offset=${offset}`,
  );
  const searching = loading || query !== debounced;
  const data = searching || error ? null : response;
  const foods = data ? unifiedFoods(data) : [];

  const openWithGrams = (food: SearchFood) => {
    if (food.external && food.source !== 'fatsecret') {
      navigate(`/nutrition/food?meal=${meal}&date=${date}`, { state: { prefill: foodSelection(food).prefill, catalogSelection: food.kcal100 !== null && food.p100 !== null && food.f100 !== null && food.c100 !== null && Boolean(food.name), portionGrams: foodPortionGrams(food) } });
      return;
    }
    navigate(`/nutrition/food?meal=${meal}&date=${date}&foodId=${food.id}`, { state: { food } });
  };

  const renderRow = (food: SearchFood) => (
    <li key={food.id}>
      <button className="rn" type="button" onClick={() => openWithGrams(food)}>
        <b>
          <Highlighted text={food.name} query={debounced} />
        </b>
        {displayFoodBrand(food.brand) ? <small>{displayFoodBrand(food.brand)}</small> : null}
        {foodPortionGrams(food) !== null ? <small>Порция · {formatDecimal(foodPortionGrams(food)!)} г</small> : null}
        <small>{foodNutritionLabel(food)}</small>
      </button>
      <button
        className="qa"
        type="button"
        onClick={() => openWithGrams(food)}
        aria-label={`Добавить «${food.name}» в ${MEAL_TITLE[meal].toLowerCase()}`}
      >
        +
      </button>
    </li>
  );

  return (
    <Screen>
      <div className="sbar" style={{ paddingTop: 18 }}>
        <label className="srch" style={{ marginTop: 0 }}>
          <SearchIcon />
          <span className="vh">Поиск продукта</span>
          <input
            type="search"
            value={query}
            onChange={(event) => {setOffset(0);setQuery(event.target.value);}}
            placeholder="Найти продукт или блюдо"
            autoFocus
            autoComplete="off"
            maxLength={120}
          />
          {query ? (
            <button
              className="clr"
              type="button"
              onClick={() => {setOffset(0);setQuery('');}}
              aria-label="Очистить"
            >
              ×
            </button>
          ) : null}
        </label>
        <button className="cnl" type="button" onClick={() => navigate(-1)}>
          Отмена
        </button>
      </div>

      <span className="ctx" style={{ marginTop: 12 }}>
        В «{MEAL_TITLE[meal]}» · {date ? formatShortDate(date) : 'сегодня'}
      </span>

      {searching ? <Loader label="Ищем…" /> : null}
      {error ? <p className="err" role="alert">{error}</p> : null}
      {data?.externalError ? <p className="err" role="alert">{data.externalError}</p> : null}
      {debounced.trim().length < 3 ? <p className="nt">Для расширенного поиска введите от 3 символов.</p> : null}

      {foods.length > 0 ? <ul className="res">{foods.map(renderRow)}</ul> : null}

      {data && (offset > 0 || data.hasMore?.recent || data.hasMore?.catalog) ? <nav className="food-pagination" aria-label="Страницы продуктов">
        <button type="button" className="pill-btn" disabled={offset === 0} onClick={() => setOffset(Math.max(0,offset-12))}>Назад</button>
        <button type="button" className="pill-btn" aria-label="Следующие продукты" disabled={!data.hasMore?.recent && !data.hasMore?.catalog} onClick={() => setOffset(offset+12)}>Далее</button>
      </nav> : null}

      {data && !data.externalError && foods.length === 0 && !searching ? (
        <p className="empty">Ничего не нашли</p>
      ) : null}

      <div className="nf" style={{ paddingBottom: 24 }}>
        <div className="nt">Не нашли нужное?</div>
        <div className="row2">
          <button
            className="pill-btn"
            type="button"
            onClick={() => navigate(`/nutrition/food?meal=${meal}&date=${date}`)}
          >
            <img src="/assets/ui/tab-food.webp" alt="" />
            Вручную
          </button>
          <button
            className="pill-btn"
            type="button"
            onClick={() => navigate(`/nutrition/scan?meal=${meal}&date=${date}`)}
          >
            <BarcodeIcon />
            Сканировать
          </button>
        </div>
      </div>
    </Screen>
  );
}
